// @flow

import React, { type Node, useState, useEffect, useCallback } from 'react';
import styled, { type StyledComponent } from 'styled-components';
import ReactModal from 'react-modal';
import { __ } from '../../../i18n/l10n';
import { colorPalette, media, sizes } from '../../../styles/GlobalStyle';
import { Icon } from './Icon';

if (process.env.NODE_ENV !== 'test') ReactModal.setAppElement('#root');

type ModalRawProps = {|
  className?: string,
  children: Node,
  isOpen?: boolean,
  onClose: (status: boolean) => void,
  contentLabel?: string,
|};

const ModalRaw = ({
  children,
  isOpen = false,
  contentLabel,
  onClose,
  className,
}: ModalRawProps) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const closeModal = useCallback(() => {
    setModalIsOpen(false);
    if (onClose) {
      onClose(false);
    }
  }, [onClose]);

  const onKeyUp = useCallback(
    (event) => {
      if (event.isComposing || event.keyCode === 27 || event.key === 'Escape') {
        closeModal();
      }
    },
    [closeModal]
  );

  useEffect(() => {
    if (isOpen !== modalIsOpen) {
      setModalIsOpen(isOpen);
    }
    if (isOpen && modalIsOpen) {
      window.addEventListener('keyup', onKeyUp, false);
    } else if (!isOpen && !modalIsOpen) {
      window.removeEventListener('keyup', onKeyUp, false);
    }
    return () => window.removeEventListener('keyup', onKeyUp);
  }, [isOpen, modalIsOpen, onKeyUp]);

  return (
    <ReactModal
      className={className}
      closeTimeoutMS={200}
      isOpen={modalIsOpen}
      onAfterClose={closeModal}
      contentLabel={contentLabel}
    >
      <div className="modal-header">
        <button className="close-button" type="button" onClick={closeModal}>
          <Icon name="close" />
          {__('Close')}
        </button>
      </div>
      <div className="modal-content">{children}</div>
    </ReactModal>
  );
};

export const Modal: StyledComponent<ModalRawProps, {}, typeof ModalRaw> = styled(ModalRaw)`
  position: relative;
  top: 50%;
  left: 50%;
  right: auto;
  bottom: auto;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  max-width: 1024px;
  background-color: ${colorPalette.dark.active};
  border: none;
  color: ${colorPalette.dark.foreGround};
  outline: none;
  z-index: 10;

  .modal-header {
    position: absolute;
    left: ${sizes.padding};
    top: 0;
    width: calc(100% - ${sizes.padding} * 2);
    height: calc(${sizes.padding} * 2 + 22px);
    background-color: ${colorPalette.dark.active};
  }

  .close-button {
    position: absolute;
    left: 0;
    top: ${sizes.padding};
    display: flex;
    align-items: center;
    background: none;
    border: none;
    text-transform: uppercase;
    color: inherit;
    padding: 0;
    margin-bottom: calc(${sizes.padding} * 2);
    cursor: pointer;

    svg {
      margin-right: 10px;
    }
  }

  .modal-content {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    overflow: auto;
    padding: calc(${sizes.padding} * 2 + 22px) ${sizes.padding} ${sizes.padding};
    z-index: -1;
  }

  ${media.greaterThan('desktop')`
    width: 80vw;
    height: 80vh;

    .modal-header {
      left: ${sizes.paddingBig};
      width: calc(100% - ${sizes.paddingBig} * 2);
      height: 140px;
    }
    
    .close-button {
      top: ${sizes.paddingBig};
    }

    .modal-content {
      top: 1px;
      height: calc(100% - 1px);
      padding: calc(${sizes.paddingBig} * 2) ${sizes.paddingBig} ${sizes.paddingBig};
    }
  `}
`;
